import Footer from '@/components/Footer';
import PassengerInfo from '@/components/purchase/checkout/PassengerInfo';
import { updatePassengersLoadingSelector } from '@/features/loading/loadingSelectors';
import { getManageFlowBookingById } from '@/features/manage/manageActions';
import { manageFlowBookingSelector } from '@/features/manage/manageSelectors';
import { updateBookingPassengers } from '@/features/purchase/purchaseActions';
import { usePageTitle } from '@/hooks/usePageTitle';
import { usePersonFieldRenderer } from '@/hooks/usePersonFieldRenderer';
import { useTranslation } from '@/i18n';
import { TransText } from '@/i18n/trans/text';
import { useDispatch, useSelector } from '@/store/utils';
import { isTicketBooking } from '@/utils/booking';
import { cn } from '@/utils/cn';
import { getPassengerName } from '@/utils/passenger';
import { createCheckoutPassengerSchema } from '@/utils/zodSchema';
import { Button, Divider, Icons, Typography } from '@fleet/webshop-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, useMemo } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';

const EditPassengerDetails: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const booking = useSelector(manageFlowBookingSelector);
  const updating = useSelector(updatePassengersLoadingSelector);
  const { t } = useTranslation();
  const { passengerAdmissionFields, passengerTravelPassFields } =
    usePersonFieldRenderer();

  usePageTitle('editPassengerDetails');

  const passengerFields = isTicketBooking(booking)
    ? passengerAdmissionFields
    : passengerTravelPassFields;

  const schema = useMemo(
    () =>
      z.object({
        passengers: z.array(createCheckoutPassengerSchema(t, passengerFields)),
      }),
    [t, passengerFields]
  );

  type EditPassengerDetailsValues = z.infer<typeof schema>;

  const form = useForm<EditPassengerDetailsValues>({
    mode: 'onBlur',
    resolver: zodResolver(schema),
    defaultValues: {
      passengers:
        booking?.passengers?.map((p) => ({
          id: p.id ?? '',
          externalReference: p.externalReference ?? '',
          firstName: p.firstName ?? '',
          lastName: p.lastName ?? '',
          email: p.contactInformation?.emailAddress ?? '',
          phoneNumber: p.contactInformation?.phoneNumber ?? '',
          birthDate: p.birthDate ?? '',
          gender: p.gender ?? '',
        })) ?? [],
    },
  });

  const { fields } = useFieldArray({
    control: form.control,
    name: 'passengers',
  });
  const passengers = form.watch('passengers');

  const onSubmit = async (values: EditPassengerDetailsValues) => {
    if (!booking?.id) return;

    try {
      await dispatch(
        updateBookingPassengers({
          bookingId: booking.id,
          passengers: values.passengers,
        })
      ).unwrap();
      await dispatch(getManageFlowBookingById(booking.id)).unwrap();
      toast.success(<TransText i18nKey="editPassengerDetailsSuccess" />);
      navigate(-1);
    } catch {
      toast.error(<TransText i18nKey="editPassengerDetailsError" />);
    }
  };

  console.log('[EditPassengerDetails] Guard check:', {
    locationState: location.state,
    fromBookingDetails: location.state?.fromBookingDetails,
    hasBooking: !!booking,
    bookingId: booking?.id,
    pathname: location.pathname,
  });

  if (!location.state?.fromBookingDetails || !booking) {
    console.log('[EditPassengerDetails] Redirecting to /manage-booking');
    return <Navigate to="/manage-booking" replace />;
  }

  return (
    <div className="pb-16 laptop:pb-4">
      <div className="mx-auto flex max-w-laptop items-center gap-2 p-4">
        <Button
          data-testid="go-back-button"
          variant="icon"
          aria-label={t('text.goBack')}
          onClick={() => navigate(-1)}
          className="w-fit"
        >
          <Icons.arrowLeft />
        </Button>
        <div className="flex items-baseline gap-1">
          <Typography variant="heading1-light">
            <>
              <TransText i18nKey="editPassengerDetails" />
              &#58;
            </>
          </Typography>
          <Typography variant="heading1-light">
            <TransText i18nKey="booking" />
          </Typography>
          <Typography variant="heading1">{booking?.code}</Typography>
        </div>
      </div>
      <div className="mx-auto max-w-laptop px-4">
        <Divider variant="medium" />
      </div>

      <div className="mx-auto mt-8 max-w-mobile px-4">
        <FormProvider {...form}>
          <form
            id="edit-passenger-details-form"
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-6"
          >
            {fields.map(({ id }, index) => {
              const passenger = passengers[index];

              return (
                <div
                  key={id}
                  className={cn('flex flex-col gap-3', {
                    'border-b border-neutral-pale pb-3':
                      index < fields.length - 1,
                  })}
                  data-testid={`edit-passengers-${index}-content`}
                >
                  <div>
                    <Typography
                      variant="body1-bold"
                      className="whitespace-normal break-all text-dark"
                      as="h3"
                    >
                      {getPassengerName(passenger, index)}
                    </Typography>
                  </div>
                  <PassengerInfo
                    formFields={passengerFields}
                    prefix={`passengers[${index}]`}
                  />
                </div>
              );
            })}
          </form>
        </FormProvider>
      </div>

      <Footer
        actionButtons={
          <Button
            type="submit"
            form="edit-passenger-details-form"
            variant="primary"
            data-testid="edit-passengers-submit"
            className="w-full laptop:w-fit"
            startAdornment={<Icons.check className="h-4 w-4" />}
            isDisabled={!form.formState.isDirty}
            isPending={updating}
          >
            <TransText i18nKey="confirm" />
          </Button>
        }
      />
    </div>
  );
};

export default EditPassengerDetails;
